import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { delegateApi } from "../lib/delegateApi";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Loader2, Lock, CalendarDays, Activity, Search, Plus, Stethoscope, Clock, X, HeartPulse, Send, MessageCircle, UserPlus } from "lucide-react";

const waDigits = (p) => { if (!p) return null; let d = (p + "").replace(/\D/g, ""); if (!d) return null; if (d.length === 10) d = "52" + d; return d; };

const VITALS = [
  ["weight", "Peso (kg)", "number"], ["height", "Talla (cm)", "number"], ["head_circ", "P. cefálico (cm)", "number"],
  ["temperature", "Temp (°C)", "number"], ["heart_rate", "FC (lpm)", "number"], ["resp_rate", "FR (rpm)", "number"],
  ["spo2", "SatO₂ (%)", "number"], ["blood_pressure", "TA (mmHg)", "text"],
];

export default function DelegateApp({ role }) {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const existing = localStorage.getItem("delegate_token");
    if (existing) {
      delegateApi.get("/delegate/context").then((r) => setCtx(r.data)).catch(() => localStorage.removeItem("delegate_token")).finally(() => setChecking(false));
    } else setChecking(false);
  }, []);

  const authenticate = async () => {
    if (!pin.trim()) return;
    setBusy(true);
    try {
      const r = await delegateApi.post("/delegate/auth", { token, pin });
      localStorage.setItem("delegate_token", r.data.session_token);
      setCtx(r.data);
      toast.success(`Bienvenido — ${r.data.doctor_name || ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Código o PIN inválido"); }
    finally { setBusy(false); }
  };

  if (checking) return <div className="min-h-screen grid place-items-center bg-stone-50"><Loader2 className="animate-spin text-brand-sage" /></div>;

  if (!ctx) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-br from-brand-sage/10 to-brand-sand/20 p-6">
        <Toaster />
        <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-sm text-center" data-testid="delegate-pin-screen">
          <div className="w-14 h-14 rounded-2xl bg-brand-sage/15 text-brand-sage grid place-items-center mx-auto mb-4"><Lock size={26} /></div>
          <h1 className="font-outfit font-bold text-xl text-brand-ink">Acceso {role === "enfermeria" ? "Enfermería" : "Asistente"}</h1>
          <p className="text-sm text-muted-foreground mt-1">Ingresa el PIN que te compartió el médico.</p>
          <input data-testid="delegate-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => e.key === "Enter" && authenticate()} inputMode="numeric" placeholder="PIN"
            className="mt-5 w-full text-center tracking-[0.4em] text-2xl font-bold border-2 border-stone-200 rounded-2xl py-3 focus:border-brand-sage outline-none" />
          <button data-testid="delegate-pin-submit" onClick={authenticate} disabled={busy}
            className="mt-4 w-full bg-brand-sage text-white font-outfit font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Entrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Toaster />
      <header className="bg-brand-ink text-white px-5 py-4 flex items-center gap-3 sticky top-0 z-10">
        {ctx.role === "enfermeria" ? <HeartPulse size={22} /> : <CalendarDays size={22} />}
        <div>
          <p className="font-outfit font-bold">{ctx.role === "enfermeria" ? "Enfermería" : "Asistente"}</p>
          <p className="text-xs text-white/70">{ctx.clinic_name || ctx.doctor_name}</p>
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-4">
        {ctx.role === "enfermeria" ? <NurseView /> : <AssistantView ctx={ctx} />}
      </main>
    </div>
  );
}

function PatientPicker({ onPick, mode, onNew }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      delegateApi.get(`/delegate/patients-lite${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setList(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="delegate-patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar paciente…"
          className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 outline-none focus:border-brand-sage" />
      </div>
      {onNew && (
        <button data-testid="assistant-new-patient-btn" onClick={onNew}
          className="mt-3 w-full flex items-center justify-center gap-2 bg-brand-sage/10 text-brand-sageDark font-semibold py-3 rounded-2xl border border-dashed border-brand-sage/40 hover:bg-brand-sage/15">
          <UserPlus size={17} /> Registrar nuevo paciente
        </button>
      )}
      <div className="mt-3 space-y-2" data-testid="delegate-patient-list">
        {list.map((p) => (
          <button key={p.patient_id} data-testid={`delegate-patient-${p.patient_id}`} onClick={() => onPick(p)}
            className="w-full text-left bg-white rounded-2xl border border-stone-100 p-3.5 hover:border-brand-sage transition-all">
            <p className="font-semibold text-brand-ink">{p.name}</p>
            <p className="text-xs text-muted-foreground">{mode === "nurse" ? (p.age?.text || "") : (p.phone || "Sin teléfono")} {p.record_number ? `· ${p.record_number}` : ""}</p>
          </button>
        ))}
        {list.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Sin pacientes.</p>}
      </div>
    </div>
  );
}

function NurseView() {
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState({});
  const [ready, setReady] = useState(true);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const payload = { patient_id: patient.patient_id };
    VITALS.forEach(([k, , type]) => {
      if (form[k] !== undefined && form[k] !== "") payload[k] = type === "number" ? Number(form[k]) : form[k];
    });
    payload.note = form.note || undefined;
    payload.mark_ready = ready;
    if (Object.keys(payload).filter((k) => !["patient_id", "note", "mark_ready"].includes(k)).length === 0) { toast.error("Captura al menos un dato"); return; }
    setSaving(true);
    try {
      await delegateApi.post("/delegate/vitals", payload);
      toast.success(ready ? "Signos enviados · paciente en sala de espera ✓" : "Signos enviados al médico ✓");
      setForm({}); setPatient(null); setReady(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setSaving(false); }
  };
  if (!patient) return <><h2 className="font-outfit font-bold text-brand-ink mb-3 flex items-center gap-2"><Activity size={18} /> Selecciona un paciente</h2><PatientPicker mode="nurse" onPick={setPatient} /></>;
  return (
    <div data-testid="nurse-vitals-form">
      <button onClick={() => setPatient(null)} className="text-sm text-brand-sage font-semibold mb-3">← Cambiar paciente</button>
      <div className="bg-white rounded-2xl border border-stone-100 p-4">
        <p className="font-outfit font-bold text-brand-ink">{patient.name}</p>
        <p className="text-xs text-muted-foreground mb-4">{patient.age?.text || ""}</p>
        <div className="grid grid-cols-2 gap-3">
          {VITALS.map(([k, label, type]) => (
            <div key={k}>
              <label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{label}</label>
              <input data-testid={`nurse-field-${k}`} type={type} step={type === "number" ? "any" : undefined} value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage" />
            </div>
          ))}
        </div>
        <label className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50 mt-3 block">Nota (opcional)</label>
        <textarea data-testid="nurse-field-note" value={form.note ?? ""} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={2}
          className="w-full mt-1 px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-brand-sage" />
        <label data-testid="nurse-ready-toggle" className="flex items-center gap-2 mt-3 cursor-pointer select-none">
          <input type="checkbox" checked={ready} onChange={(e) => setReady(e.target.checked)} className="w-4 h-4 accent-brand-sage" />
          <span className="text-sm font-semibold text-brand-ink">Marcar como listo para pasar al médico</span>
        </label>
        <button data-testid="nurse-save-vitals" onClick={save} disabled={saving}
          className="mt-4 w-full bg-brand-sage text-white font-outfit font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {ready ? "Enviar y pasar al médico" : "Enviar al médico"}
        </button>
      </div>
    </div>
  );
}

function NewPatientForm({ onCreated, onCancel }) {
  const [f, setF] = useState({ name: "", sex: "M", birth_date: "", guardian_name: "", guardian_phone: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim() || !f.birth_date) { toast.error("Nombre y fecha de nacimiento son obligatorios"); return; }
    setSaving(true);
    try {
      const r = await delegateApi.post("/delegate/patients", f);
      toast.success("Paciente registrado ✓");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="assistant-new-patient-form">
      <button onClick={onCancel} className="text-sm text-brand-sage font-semibold mb-3">← Volver</button>
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <h2 className="font-outfit font-bold text-brand-ink flex items-center gap-2"><UserPlus size={18} /> Nuevo paciente</h2>
        <div><label className="text-xs font-bold uppercase text-brand-ink/50">Nombre completo</label>
          <input data-testid="np-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold uppercase text-brand-ink/50">Sexo</label>
            <select data-testid="np-sex" value={f.sex} onChange={(e) => set("sex", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage bg-white">
              <option value="M">Masculino</option><option value="F">Femenino</option></select></div>
          <div><label className="text-xs font-bold uppercase text-brand-ink/50">Fecha de nacimiento</label>
            <input data-testid="np-birth" type="date" value={f.birth_date} onChange={(e) => set("birth_date", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage" /></div>
        </div>
        <div><label className="text-xs font-bold uppercase text-brand-ink/50">Nombre del tutor</label>
          <input data-testid="np-guardian" value={f.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage" /></div>
        <div><label className="text-xs font-bold uppercase text-brand-ink/50">WhatsApp del tutor</label>
          <input data-testid="np-phone" inputMode="tel" value={f.guardian_phone} onChange={(e) => set("guardian_phone", e.target.value)} placeholder="10 dígitos" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-brand-sage" /></div>
        <button data-testid="np-save" onClick={save} disabled={saving} className="w-full bg-brand-sage text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Registrar
        </button>
      </div>
    </div>
  );
}

function AssistantView({ ctx }) {
  const [appts, setAppts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newPatient, setNewPatient] = useState(false);
  const [form, setForm] = useState({ patient: null, scheduled_at: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const load = () => delegateApi.get("/delegate/appointments").then((r) => setAppts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const waHref = (a) => {
    const num = waDigits(a.guardian_phone);
    if (!num) return null;
    const who = a.guardian_name ? `${a.guardian_name}, ` : "";
    const when = fmt(a.scheduled_at);
    const msg = `Hola ${who}le recordamos la cita de ${a.patient_name} con ${ctx?.doctor_name || "su médico"} en ${ctx?.clinic_name || "el consultorio"} el ${when}.` + (a.reason ? ` Motivo: ${a.reason}.` : "") + ` Si necesita reprogramar, por favor avísenos. ¡Gracias!`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };
  const create = async () => {
    if (!form.patient || !form.scheduled_at) { toast.error("Elige paciente y fecha"); return; }
    setSaving(true);
    try {
      await delegateApi.post("/delegate/appointments", { patient_id: form.patient.patient_id, scheduled_at: new Date(form.scheduled_at).toISOString(), reason: form.reason });
      toast.success("Cita agendada"); setShowNew(false); setForm({ patient: null, scheduled_at: "", reason: "" }); load();
    } catch { toast.error("No se pudo agendar"); } finally { setSaving(false); }
  };
  const cancel = async (a) => { if (!window.confirm("¿Cancelar esta cita?")) return; await delegateApi.put(`/delegate/appointments/${a.appointment_id}`, { status: "cancelada" }); toast.success("Cita cancelada"); load(); };

  if (showNew) {
    if (newPatient) return <NewPatientForm onCancel={() => setNewPatient(false)} onCreated={(p) => { setForm((f) => ({ ...f, patient: p })); setNewPatient(false); }} />;
    if (!form.patient) return (
      <div><button onClick={() => setShowNew(false)} className="text-sm text-brand-sage font-semibold mb-3">← Volver</button>
        <h2 className="font-outfit font-bold text-brand-ink mb-3">Elige paciente</h2>
        <PatientPicker mode="assistant" onNew={() => setNewPatient(true)} onPick={(p) => setForm((f) => ({ ...f, patient: p }))} /></div>
    );
    return (
      <div data-testid="assistant-new-form">
        <button onClick={() => setForm((f) => ({ ...f, patient: null }))} className="text-sm text-brand-sage font-semibold mb-3">← Cambiar paciente</button>
        <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
          <p className="font-semibold text-brand-ink">{form.patient.name}</p>
          <div><label className="text-xs font-bold uppercase text-brand-ink/50">Fecha y hora</label>
            <input data-testid="assistant-datetime" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none" /></div>
          <div><label className="text-xs font-bold uppercase text-brand-ink/50">Motivo</label>
            <input data-testid="assistant-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className="w-full mt-1 px-3 py-2.5 rounded-xl border border-stone-200 outline-none" /></div>
          <button data-testid="assistant-save-appt" onClick={create} disabled={saving} className="w-full bg-brand-sage text-white font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Agendar</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="font-outfit font-bold text-brand-ink flex items-center gap-2"><CalendarDays size={18} /> Próximas citas</h2>
        <div className="flex gap-2">
          <button data-testid="assistant-new-patient-quick" onClick={() => { setShowNew(true); setNewPatient(true); }} className="bg-white border border-brand-sage/40 text-brand-sageDark text-sm font-semibold px-3.5 py-2 rounded-xl flex items-center gap-1.5"><UserPlus size={16} /> Paciente</button>
          <button data-testid="assistant-new-btn" onClick={() => { setShowNew(true); setNewPatient(false); }} className="bg-brand-sage text-white text-sm font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5"><Plus size={16} /> Agendar</button>
        </div>
      </div>
      <div className="space-y-2" data-testid="assistant-appt-list">
        {appts.filter((a) => a.status !== "cancelada").map((a) => {
          const wa = waHref(a);
          return (
            <div key={a.appointment_id} className="bg-white rounded-2xl border border-stone-100 p-3.5 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-brand-sage/10 text-brand-sage grid place-items-center shrink-0"><Clock size={16} /></div>
              <div className="flex-1 min-w-0"><p className="font-semibold text-brand-ink truncate">{a.patient_name}</p><p className="text-xs text-muted-foreground">{fmt(a.scheduled_at)}{a.reason ? ` · ${a.reason}` : ""}</p></div>
              {wa && (
                <a data-testid={`assistant-wa-${a.appointment_id}`} href={wa} target="_blank" rel="noopener noreferrer" title="Recordar por WhatsApp"
                  className="shrink-0 w-9 h-9 rounded-full bg-[#25D366]/10 text-[#128C7E] grid place-items-center hover:bg-[#25D366]/20"><MessageCircle size={17} /></a>
              )}
              <button data-testid={`assistant-cancel-${a.appointment_id}`} onClick={() => cancel(a)} className="text-muted-foreground hover:text-red-600 p-2 shrink-0"><X size={16} /></button>
            </div>
          );
        })}
        {appts.filter((a) => a.status !== "cancelada").length === 0 && <p className="text-center text-sm text-muted-foreground py-8">No hay citas próximas.</p>}
      </div>
    </div>
  );
}
